import React from 'react';
import styled from 'styled-components';

const CardLink = styled.a`
  text-decoration: none;
  display: block;
  max-width: 440px;
  width: 100%;
  margin-top: 28px;
  user-select: none;
  cursor: pointer;

  @media (max-width: 768px) {
    margin-top: 24px;
    max-width: 100%;
  }
`;

const AppInfoBox = styled.div`
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 22px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 16px -2px rgba(15, 23, 42, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  ${CardLink}:hover & {
    box-shadow: 0 8px 24px -4px rgba(15, 23, 42, 0.08);
    transform: translateY(-2px);
    border-color: #cbd5e1;
  }

  @media (max-width: 480px) {
    padding: 12px 16px;
    border-radius: 18px;
  }
`;

const LogoContainer = styled.div`
  width: 64px;
  height: 64px;
  min-width: 64px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);

  @media (max-width: 480px) {
    width: 54px;
    height: 54px;
    min-width: 54px;
    border-radius: 14px;
  }
`;

const LogoImg = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
  }
`;

const Details = styled.div`
  margin-left: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;

  @media (max-width: 480px) {
    margin-left: 12px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const Title = styled.span`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const Badge = styled.span`
  background: #e6f8ee;
  color: #10b981;
  border: 1px solid #a7f3d0;
  border-radius: 9999px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  font-family: 'Plus Jakarta Sans', sans-serif;
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 11px;
    padding: 1px 8px;
  }
`;

const Subtitle = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  margin-top: 4px;
  line-height: 1.3;

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 6px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13.5px;
  line-height: 1;

  @media (max-width: 480px) {
    font-size: 12.5px;
    margin-top: 5px;
  }
`;

const Rating = styled.span`
  font-weight: 700;
  color: #d97706;
`;

const Star = styled.span`
  color: #f59e0b;
  font-size: 14px;
  margin-left: 3px;
`;

const Bullet = styled.span`
  color: #cbd5e1;
  margin: 0 8px;
  font-size: 12px;
`;

const Downloads = styled.span`
  font-weight: 600;
  color: #334155;
`;

const PlayButton = styled.div`
  margin-top: 14px;
  background: #070d22;
  border-radius: 26px;
  padding: 14px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 8px 20px -4px rgba(7, 13, 34, 0.3);
  transition: all 0.2s ease;

  ${CardLink}:hover & {
    background: #0c1538;
    box-shadow: 0 12px 28px -4px rgba(7, 13, 34, 0.4);
    transform: translateY(-2px);
  }

  @media (max-width: 480px) {
    padding: 12px 20px;
    border-radius: 22px;
    margin-top: 12px;
  }
`;

const PlayLeft = styled.div`
  display: flex;
  align-items: center;
`;

const PlayIcon = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;

  @media (max-width: 480px) {
    width: 24px;
    height: 24px;
  }
`;

const PlayTextCol = styled.div`
  margin-left: 14px;
  display: flex;
  flex-direction: column;

  @media (max-width: 480px) {
    margin-left: 10px;
  }
`;

const PlaySubtext = styled.span`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  color: #94a3b8;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  line-height: 1;

  @media (max-width: 480px) {
    font-size: 9.5px;
  }
`;

const PlayTitle = styled.span`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 21px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.15;
  margin-top: 3px;

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const ArrowIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  ${CardLink}:hover & {
    transform: translateX(4px);
  }
`;

const TrustRow = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 480px) {
    margin-top: 10px;
  }
`;

const TrustText = styled.span`
  margin-left: 8px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13.5px;
  font-weight: 600;
  color: #15803d;
  letter-spacing: -0.1px;

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const AppDownloadCard = () => {
  return (
    <CardLink
      href="https://los-prod.dailype.in/app/link"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Get ClickPe on Google Play"
    >
      <AppInfoBox>
        <LogoContainer>
          <LogoImg src="/images/logo2.png" alt="ClickPe Logo" />
        </LogoContainer>
        <Details>
          <TitleRow>
            <Title>ClickPe</Title>
            <Badge>Official App</Badge>
          </TitleRow>
          <Subtitle>Education Fee Payment & More</Subtitle>
          <StatsRow>
            <Rating>4.7</Rating>
            <Star>★</Star>
            <Bullet>•</Bullet>
            <Downloads>10K+ Downloads</Downloads>
          </StatsRow>
        </Details>
      </AppInfoBox>

      <PlayButton>
        <PlayLeft>
          <PlayIcon src="/images/google-play.png" alt="Google Play Icon" />
          <PlayTextCol>
            <PlaySubtext>GET IT ON</PlaySubtext>
            <PlayTitle>Google Play</PlayTitle>
          </PlayTextCol>
        </PlayLeft>
        <ArrowIconWrapper>
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#FFFFFF"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        </ArrowIconWrapper>
      </PlayButton>

      <TrustRow>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#16A34A"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
          <path d="m9 12 2 2 4-4"></path>
        </svg>
        <TrustText>Google Play Protect Verified • 100% Secure</TrustText>
      </TrustRow>
    </CardLink>
  );
};

export default AppDownloadCard;
